/**
 * The account-deletion classification table: every category of per-user
 * data in the product, mapped to the policy the deletion cascade applies
 * to it. This file is the source of truth the deletion manifest
 * (`admin/manifest.ts`) is checked against — a category with no
 * implementing task, or a task with no category, fails the completeness
 * test.
 *
 * Where each category physically lives is recorded per collection in
 * `collectionCoverage.ts`, and each task's module doc names the writers
 * its paths were checked against. The legal grounding for each policy is
 * the `exemption` on its entry.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/3263
 */
import {z} from 'zod';

/**
 * What the cascade does with a category of data.
 *
 * - `delete` — hard-delete the documents/objects/nodes.
 * - `scrub` — the record survives, but every field that IDENTIFIES the
 *   deleted user — display name, email address, photo URL — is removed
 *   or anonymized in place, and their membership of any shared array or
 *   map is removed. Uid fields are deliberately left untouched: a bare
 *   uid identifies nobody once the Auth record is gone, and overwriting
 *   it would forge a foreign key and collapse every deleted account onto
 *   one value. See `admin/tasks/scrubValues.ts` for the full reasoning
 *   and `admin/residueScan.ts` for what this makes "residue" mean.
 * - `retain` — intentionally kept, unmodified, under a documented legal
 *   exemption. Retain categories MUST carry an {@link AccountDeletionCategory.exemption}.
 */
export const DELETION_POLICIES = ['delete', 'scrub', 'retain'] as const;

/** Zod schema for {@link DeletionPolicy}. */
export const DeletionPolicySchema = z.enum(DELETION_POLICIES);

/** What the cascade does with a category of data. See {@link DELETION_POLICIES}. */
export type DeletionPolicy = z.infer<typeof DeletionPolicySchema>;

/**
 * Union of every category key in {@link ACCOUNT_DELETION_CLASSIFICATION},
 * derived from the table itself so the two can never drift.
 *
 * Consumers that name a category — `LOCKED_KEY_POLICY_PAIRS` in
 * `classification.test.ts`, `categoryKeys` in `collectionCoverage.ts` —
 * type against this union, which turns a mistyped key into a compile
 * error instead of a silently-unmatched string.
 */
export type CategoryKey = (typeof CLASSIFICATION_ENTRIES)[number]['key'];

/**
 * One category of per-user data and the policy applied to it.
 */
export interface AccountDeletionCategory {
  /** Stable kebab-case identifier, used in audit `categorySummary` keys. */
  key: CategoryKey;
  /** Human-readable name for reports and the retention-policy doc. */
  title: string;
  /** What the cascade does with this category. */
  policy: DeletionPolicy;
  /**
   * What the category covers. Where the data lives (exact collections /
   * paths) is defined by the implementing manifest tasks and the
   * data-model audit doc, not restated here.
   */
  description: string;
  /**
   * The legal exemption justifying retention. Present exactly when
   * `policy === 'retain'` (enforced by `classification.test.ts`).
   */
  exemption?: string;
  /**
   * Ids of the manifest tasks (`admin/manifest.ts`) that implement this
   * category. Empty exactly when `policy === 'retain'` (enforced by
   * `classification.test.ts`); the manifest completeness test asserts
   * every listed id exists.
   */
  taskIds: readonly string[];
}

/**
 * The recorded basis for retaining a record whose only link to the
 * person is a bare uid.
 *
 * Stated plainly rather than as a clean statutory carve-out, because it
 * is not one: a retained uid is pseudonymous, not anonymous (see
 * `abuse-reports-about-user` and `deletion-tombstone` below). The
 * posture is the one every scrubbed uid field in this table already
 * rests on — defensible only while nothing retained pairs a uid with an
 * identity.
 */
const RETAINED_BARE_UID_EXEMPTION =
  'Pseudonymous record with no identifying content, retained under the ' +
  'same posture as every scrubbed uid field in this table: defensible ' +
  'only while nothing retained pairs a uid with an identity. Rests on ' +
  'the team decision that bare uids are not treated as personal data ' +
  'once the Auth record and profile are erased; the legal basis is to ' +
  'be confirmed with counsel before launch.';

/**
 * The classification entries, kept `as const` so {@link CategoryKey} can
 * be derived from them. Read {@link ACCOUNT_DELETION_CLASSIFICATION}
 * instead — it is the same array under its public type.
 */
const CLASSIFICATION_ENTRIES = [
  // ------------------------------------------------------------------
  // delete
  // ------------------------------------------------------------------
  {
    key: 'user-subtree',
    title: 'User document and all subcollections',
    policy: 'delete',
    description:
      'The users/{uid} profile document plus every subcollection under ' +
      'it, including the wildcard per-course subcollection family. ' +
      'Enumerated at runtime via listCollections(); a hardcoded list ' +
      'would drift.',
    taskIds: ['user-subtree'],
  },
  {
    key: 'uid-keyed-collections',
    title: 'Collections keyed by uid as document id',
    policy: 'delete',
    description:
      'Documents elsewhere in Firestore whose document id (or path ' +
      'segment) is the uid: diagnostics, engagement, presence, karma, ' +
      'IDE/error logs, tutor chat history, stories, teachnow, chatgpt, ' +
      'grading submissions, SL review, LLM student summaries, and ' +
      'similar. Table-driven per-course path construction.',
    taskIds: ['uid-keyed-sweeps'],
  },
  {
    key: 'showcase-submissions',
    title: 'Final-project showcase submissions',
    policy: 'delete',
    description:
      'The uid-keyed docs that make a final project publicly visible ' +
      '(submission, published mirror, finalProjects entry). Deleting ' +
      'the submission doc is also the mechanism that unlists the ' +
      'project from the gallery and (via the existing indexing ' +
      'trigger) the Typesense mirror.',
    taskIds: ['uid-keyed-sweeps'],
  },
  {
    key: 'forum-drafts',
    title: 'Forum drafts',
    policy: 'delete',
    description:
      'Unpublished forum drafts and draft rich-text docs authored by ' +
      'the user. Never visible to others, so no thread-integrity ' +
      'argument for keeping them.',
    taskIds: ['forum-scrub'],
  },
  {
    key: 'appreciation-notes',
    title: 'Appreciation notes and attempts',
    policy: 'delete',
    description:
      'Appreciation notes sent by the user, notes received by the ' +
      'user, and the held/blocked-content attempt audit entries. Free ' +
      'text in both directions is a PII risk, so both directions are ' +
      'deleted.',
    taskIds: ['appreciation-sweep'],
  },
  {
    key: 'misc-field-keyed-docs',
    title: 'Miscellaneous field-keyed documents',
    policy: 'delete',
    description:
      'Documents found by querying a uid field rather than a path: ' +
      'section-switch reasons, run-hang reports, mood surveys, ' +
      'post-section feedback, application referral hashes, and ' +
      'section-summary feedback.',
    taskIds: ['field-keyed-sweeps'],
  },
  {
    key: 'unpublished-sole-editor-projects',
    title: 'Unpublished sole-editor projects',
    policy: 'delete',
    description:
      'IDE projects where the deleted user is the only editor and the ' +
      'project was never published to the showcase: the project doc, ' +
      'code, logs, sessions, keys, style feedback, linked WhatsApp ' +
      'projects, and project storage.',
    taskIds: ['projects-multi-owner'],
  },
  {
    key: 'published-showcase-artifacts',
    title: 'Previously-published showcase artifacts',
    policy: 'delete',
    description:
      'A sole-editor project that was published to the showcase is ' +
      'deleted outright, exactly like one that never was: the artifact ' +
      'is the code the user wrote, and free-form text is precisely what ' +
      'a scrub cannot inspect. It was previously retained anonymized ' +
      'and merely unlisted (operator decision 2026-07-20), which left a ' +
      "copy of the user's own writing present but invisible; the " +
      'showcase record it was kept for is served by the research ' +
      'exports instead. Co-edited published projects are still scrubbed ' +
      'rather than deleted — see `starred-projects` and the ' +
      '`projects-multi-owner` task.',
    taskIds: ['projects-multi-owner'],
  },
  {
    key: 'certificates-public-docs',
    title: 'Public certificate documents',
    policy: 'delete',
    description:
      'The publicly-readable certificate docs, which carry the uid and ' +
      'the real name the user typed for their certificate.',
    taskIds: ['certificates'],
  },
  {
    key: 'storage-user-files',
    title: 'Cloud Storage user files',
    policy: 'delete',
    description:
      'Profile pictures, name recordings, demo/training videos, ' +
      'section-teaching screen and microphone recordings, code photos, ' +
      'project screenshots, rich-text images, and section banners ' +
      'uploaded by the user — exact objects and uid-keyed folders. ' +
      'Section banners are emitted by `sections-scrub`, not ' +
      '`storage-targets`: their only handle is the set of sections the ' +
      'user was involved in, and an earlier phase scrubs that away ' +
      '(see the cross-phase enumeration invariant on ' +
      '`DELETION_PHASES`).',
    taskIds: ['storage-targets', 'sections-scrub'],
  },
  {
    key: 'rtdb-user-nodes',
    title: 'Realtime Database user nodes',
    policy: 'delete',
    description:
      'Per-course presence/status and event nodes, teachnow queue and ' +
      'learner entries, the display name on every teachnow room the ' +
      'user was in, and waiting-room presence (display name and photo) ' +
      'in the sections the user was in. Presence is removed by ' +
      '`sections-scrub`, which holds those sections.',
    taskIds: ['rtdb-targets', 'sections-scrub'],
  },
  {
    key: 'typesense-showcase-mirror',
    title: 'Typesense showcase mirror',
    policy: 'delete',
    description:
      "The external Typesense index entry for the user's final " +
      'project. Primarily removed by the existing indexing trigger ' +
      'when the submission doc is deleted; the cascade also issues a ' +
      'direct 404-tolerant delete as a backstop.',
    taskIds: ['typesense-backstop'],
  },
  {
    key: 'connections-bilateral',
    title: 'Bilateral connections',
    policy: 'delete',
    description:
      "The user's connection/request/dismissal docs, the mirrored " +
      "copies under every peer's protected subtree (which hold " +
      'free-text contact info — the highest-risk PII in the system), ' +
      'and the pair-state docs with their history.',
    taskIds: ['connections-teardown'],
  },
  // ------------------------------------------------------------------
  // scrub
  // ------------------------------------------------------------------
  {
    key: 'forum-authorship',
    title: 'Forum post/reply authorship',
    policy: 'scrub',
    description:
      'Published forum posts and replies survive as documents so threads ' +
      'other learners took part in still read, but the title, both ' +
      '`contents` projections, and the rendered `tipTapDocs` body (JSON ' +
      'and Yjs blob) are replaced with a placeholder, and the author ' +
      'display name is anonymized. `authorUid` is kept. Bodies used to ' +
      'be preserved outright (operator decision 2026-07-20); free text ' +
      'is exactly what an identity scrub cannot inspect, so the record ' +
      'is now kept without its content.',
    taskIds: ['forum-scrub'],
  },
  {
    key: 'chat-message-identity',
    title: 'Waiting-room chat message identity',
    policy: 'scrub',
    description:
      'Chat messages are append-only by rule; the frozen display name ' +
      'and photo URL are anonymized in place and the message `uid` is ' +
      'kept. Both projections of the body — the rendered ' +
      '`contentTipTapJson` and the `contentPlainText` audit copy — are ' +
      'replaced with a placeholder, so the message keeps its place in ' +
      'the conversation without keeping what was said. Text used to be ' +
      'kept outright (operator decision 2026-07-20).',
    taskIds: ['sections-scrub'],
  },
  {
    key: 'report-note-authorship',
    title: 'Report note authorship',
    policy: 'scrub',
    description:
      'Moderation/triage notes authored by the user keep their content ' +
      'as a moderation record and their `authorUid`; the author name ' +
      'is anonymized.',
    taskIds: ['reports-scrub'],
  },
  {
    key: 'section-membership',
    title: 'Section membership and leadership fields',
    policy: 'scrub',
    description:
      'Cover entries, which carry the display name, are removed from ' +
      'the section document. Everything else on it that points at the ' +
      'user is a uid and is kept — the member array, per-week guest ' +
      'lists, and the section-leader and zoom-creator fields — so ' +
      'section sizes do not change and the document stays well-formed ' +
      'for every reader that dereferences it. A section the user led ' +
      'raises a warning audit event.',
    taskIds: ['sections-scrub'],
  },
  {
    key: 'section-participation',
    title: 'Section participation writing',
    policy: 'scrub',
    description:
      'What the user wrote about their sections: student reflections, ' +
      'SL debriefs, and legacy post-section survey responses. Each ' +
      'record is kept with its ratings and participation marks, which ' +
      'the SL feedback figures are built from, and every written answer ' +
      "is replaced with a placeholder, as forum bodies are. A debrief's " +
      'free-text summary-feedback notes are replaced the same way, and an ' +
      'unsubmitted debrief draft is deleted. These records were deleted ' +
      'outright until the team chose to keep the numbers ' +
      '(2026-09-10). The participation records that carry no free text ' +
      'are retained under `section-participation-signals`.',
    taskIds: ['sections-scrub'],
  },
  {
    key: 'chat-audit-actor',
    title: 'Chat audit actor',
    policy: 'scrub',
    description:
      'Waiting-room audit events keep their event skeleton for abuse ' +
      'forensics, and their actor uid; the actor display name is ' +
      'anonymized, and the message-text snapshot and user agent are ' +
      'nulled, since the message itself is blanked.',
    taskIds: ['chat-audit-scrub'],
  },
  {
    key: 'inbound-email-sender',
    title: 'Inbound support email sender',
    policy: 'scrub',
    description:
      'Inbound support emails/threads from the user keep their bodies ' +
      'as a support/abuse record; the sender address and name are ' +
      'anonymized. The staff-reply author uid is a uid field and is ' +
      'kept.',
    taskIds: ['inbound-email-scrub'],
  },
  {
    key: 'wanttoconnect-shard',
    title: 'Want-to-connect shard entry',
    policy: 'scrub',
    description:
      'The connections opt-in shard maps uids to booleans. The shard ' +
      'maintenance trigger only fires on user-doc updates, not ' +
      'deletes, so the entry is removed explicitly.',
    taskIds: ['wanttoconnect-shard'],
  },
  // ------------------------------------------------------------------
  // retain
  // ------------------------------------------------------------------
  {
    key: 'abuse-reports-about-user',
    title: 'Abuse reports naming the user as target',
    policy: 'retain',
    description:
      'Reports where the deleted user is the subject of an abuse ' +
      'report are kept for platform safety. CAVEAT: this is the one ' +
      'retained surface that can RE-LINK a retained uid to a person. ' +
      'The scrub leaves uid fields in place, which is only ' +
      'defensible while nothing retained pairs a uid with an ' +
      'identity; report bodies are free text and may name the ' +
      'reported user. Retained uids are therefore pseudonymous, not ' +
      'anonymous, and the residual risk is accepted under the ' +
      'exemption below rather than argued away.',
    exemption:
      'Security/fraud and legal-claims exemptions: CCPA § 1798.105(d)(2); ' +
      'GDPR Art. 17(3)(e).',
    taskIds: [],
  },
  {
    key: 'course-ban-audit',
    title: 'Course-ban audit trail',
    policy: 'retain',
    description:
      'The append-only ban audit trail (uid, scope, reason — no ' +
      'name/email) is kept so ban evasion and appeals remain ' +
      'reconstructable.',
    exemption:
      'Security/fraud and legal-claims exemptions: CCPA § 1798.105(d)(2); ' +
      'GDPR Art. 17(3)(e).',
    taskIds: [],
  },
  {
    key: 'impersonation-audit',
    title: 'Impersonation audit trail',
    policy: 'retain',
    description:
      'One record per staff impersonation attempt: the two uids, the ' +
      'course, the outcome and when. Kept so it stays possible to tell ' +
      'whether an action on an account, a deletion request included, ' +
      'came from an impersonated session.',
    exemption:
      'Security/fraud and legal-claims exemptions: CCPA § 1798.105(d)(2); ' +
      'GDPR Art. 17(3)(e).',
    taskIds: [],
  },
  {
    key: 'usage-logs',
    title: 'Model usage logs',
    policy: 'retain',
    description:
      'One record per LLM call: the uid, the model and how long it took, ' +
      'with no prompt or response text. Kept, so usage and cost figures ' +
      "built from them don't change when someone deletes their account.",
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
  {
    key: 'deletion-tombstone',
    title: 'Account-deletion tombstone and audit events',
    policy: 'retain',
    description:
      "This system's own PII-free record that the deletion happened: " +
      'the tombstone doubles as the backup-restore marker and the ' +
      'compliance evidence. Stores the uid and category/op counts, ' +
      'never names, emails, or content. It retains that uid ' +
      'indefinitely by design, and so do the scrubbed records ' +
      'elsewhere in this table — which is exactly why nothing ' +
      'retained may pair a uid with an identity.',
    exemption:
      'Compliance-evidence and backup-tombstone record: GDPR ' +
      'Art. 17(3)(b),(e); CCPA 11 CCR § 7022(b) (backup deferral), ' +
      '§ 1798.105(d)(8) (legal obligation).',
    taskIds: [],
  },
  {
    key: 'aggregate-statistics',
    title: 'Aggregate statistics',
    policy: 'retain',
    description:
      'Course-level aggregates such as karma totals. They carry no ' +
      'per-user entries, so they are not personal data. The per-user ' +
      'entries that sit behind other course figures — rosters, ' +
      'completions, certificate completions — are retained as bare ' +
      'uids under `roster-cache-shards` and `certificate-stats`.',
    exemption:
      'Aggregate/deidentified data is outside the deletion obligation: ' +
      'CCPA § 1798.140(b); GDPR Recital 26.',
    taskIds: [],
  },
  {
    key: 'roster-cache-shards',
    title: 'Roster and cache shards',
    policy: 'retain',
    description:
      'Rosters, section signups, SL-training signups, the section-signup ' +
      'SL cache, completion shards, attendance grades, the section-' +
      'attendance overview shards (`course/{courseId}/sectionAttendance`) ' +
      'and application-status shards. Every entry is a uid mapped to a ' +
      'boolean, a number or a status — nothing that names anyone — and ' +
      'they are what course rosters, completion and attendance figures ' +
      'and application counts are computed from. They were previously ' +
      "scrubbed, which shrank a course's roster by one for every " +
      'deleted account. The roster trigger in the default functions ' +
      'codebase (`onRoleChange`) would do the same when the cascade ' +
      'deletes the role docs, so it keeps the entry for a user with a ' +
      'tombstone. The uid is not a route to the person: ' +
      'connection suggestions also require an entry in the ' +
      'want-to-connect shard, which `wanttoconnect-shard` removes.',
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
  {
    key: 'certificate-stats',
    title: 'Certificate completion stats',
    policy: 'retain',
    description:
      'The certificate-stats shards map uids to completion timestamps ' +
      'and feed the completion-percent dashboard. The entry is a bare ' +
      'uid and a time, so it is kept and course completion figures do ' +
      'not move when an account is deleted. The public certificate ' +
      'docs, which carry the name the user typed, are still deleted ' +
      'under `certificates-public-docs`.',
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
  {
    key: 'forum-liked-by',
    title: 'Forum likedBy entries',
    policy: 'retain',
    description:
      "The user's likes on other people's posts and replies: a uid " +
      "mapped to `true` in each post's `likedBy` map. Kept, so like " +
      'counts and forum ranking do not change when an account is ' +
      'deleted.',
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
  {
    key: 'chat-reactions',
    title: 'Waiting-room chat reactions',
    policy: 'scrub',
    description:
      'Reaction docs carry the uid in their `{messageId}__{uid}` doc id, ' +
      'the emoji, and a denormalized copy of the reactor display name, ' +
      'which the reaction tooltip shows. Kept, so reaction counts on ' +
      "other people's messages do not change, with the display name " +
      'anonymized.',
    taskIds: ['sections-scrub'],
  },
  {
    key: 'report-seen-receipts',
    title: 'Report seen-receipts',
    policy: 'retain',
    description:
      'Per-report read receipts keyed by the uid, holding only ' +
      '`lastSeenAt`.',
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
  {
    key: 'starred-projects',
    title: 'Starred-project entries',
    policy: 'retain',
    description:
      'Starring is an `isStarred` flag on the published project doc ' +
      'itself, not an entry keyed to the account that starred it, so ' +
      "there is nothing of the deleted user's to remove here. The flag " +
      'stays or goes with its project doc under the project categories.',
    exemption:
      'Holds no data keyed to the deleted account, so no deletion ' +
      'obligation attaches to it on its own.',
    taskIds: [],
  },
  {
    key: 'section-participation-signals',
    title: 'Section participation signals',
    policy: 'retain',
    description:
      'The participation records that carry no free text: RSVP docs ' +
      '(week to boolean) and their change logs, reflection-moderation ' +
      'verdicts (model version and category scores), and the SL ' +
      "feedback page's helpfulness votes (thumbs up or down) and their " +
      'history. Kept, so attendance forecasts, moderation history and ' +
      'feedback-quality figures do not change. What the user wrote is ' +
      'blanked under `section-participation`.',
    exemption: RETAINED_BARE_UID_EXEMPTION,
    taskIds: [],
  },
] as const;

/**
 * The full classification. Order groups by policy (delete, scrub,
 * retain) for readability; order carries no runtime meaning.
 *
 * This assignment is also what type-checks {@link CLASSIFICATION_ENTRIES}
 * against {@link AccountDeletionCategory} — a `retain` entry missing its
 * exemption still fails in `classification.test.ts`, which checks the
 * rules a type cannot express.
 */
export const ACCOUNT_DELETION_CLASSIFICATION: readonly AccountDeletionCategory[] =
  CLASSIFICATION_ENTRIES;
